import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { action, observable } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { displaySurveyPointsClientSide } from '../Maps/SETMapFunctions/loadLayers';
import { resetMap } from '../Maps/SETMapFunctions/mapOperations';
import { preloadExistingSurvey } from '../SOSurveys/SOSurveysVM';
import { saveXYZSurvey, cancelEditXYZSurvey, saveTinSurface } from '../SurveyListDataGrid/SurveyListDataGridVM';
import { saveSOSurveyHandler } from '../SOSurveyActions/SOSurveyActionsVM';
import { DesignContainerVM } from '../../Design/DesignContainer/DesignContainerVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { SetSurveyController, SurfaceDefController, SetSurveyStationOffsetController } from 'cd-eft/services/eftSetApi';
import { XyzSurvey } from 'cd-eft/components/Design/BreaklineTools/model/XyzSurvey';
import { GroundPoint } from 'cd-eft/components/Design/BreaklineTools/model/GroundPoint';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';

const setStore = stores.AppStore.surveyEngineeringStore;
const designStore = stores.AppStore.designStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;

export const renderer = {
  type: 'simple',
  symbol: {
    type: 'simple-marker',
    color: 'blue',
    size: '4',
    outline: null,
  },
};

export class SurveySummaryVM {
  @observable props;
  constructor(props: any) {
    this.props = props;
  }

  surveyTypeName = {
    [appConfig.hardCodes.surveyTypeXYZ]: 'XYZ', // 1
    [appConfig.hardCodes.surveyTypeSpredsheet]: 'So-Spreadsheet', // 2
    [appConfig.hardCodes.surveyTypeCrossSlope]: 'So-CrossSlope', // 3
  };

  elementActions = {
    Edit: { action: this.openEditSurveyHandler,disabled:setStore.isStatePlane },
    Delete: { action: this.deleteSurveyHandler },
  };

  private _currentWkid = appConfig.tempHardCodes.wkid;

  @observable selectSurface = false;
  @observable someSurveySelected = false;
  // if one survey already open for editing and user tries to open another,
  // we place desirable survey to this.surveyToEdit
  @observable surveyToEdit: any = {};

  // delete survey dialog -->
  @observable surveyToDelete = { surveyId: '', surveyName: '', surveyType: '' };

  @action.bound setSurveyToDelete(item: any) {
    this.surveyToDelete.surveyId = item.surveyId;
    this.surveyToDelete.surveyName = item.surveyName;
    this.surveyToDelete.surveyType = item.surveyType;
  }

  @observable deleteSurveyWindowIsOpen: boolean = false;

  @action.bound toggleDeleteSurvey() {
    this.deleteSurveyWindowIsOpen = !this.deleteSurveyWindowIsOpen;
  }

  @action.bound deleteSurveyHandler(e: any, item: any) {
    this.setSurveyToDelete(item);
    this.deleteSurveyWindowIsOpen = true;
  }

  @action.bound async deleteSurvey() {
    designStore.snappingEnabled = false
    this.props.updateSnapIcon();
    this.props.toggleSnapping();
    const sessionId: string = designStore.projectsStore.currentSessionId;
    const id: string = this.surveyToDelete.surveyId;
    setStore.isLoading = true;
    this.toggleDeleteSurvey();
    try {
      await SetSurveyController.hardDeleteSurveyUsingDELETE(sessionId, id)
      if (setStore.surveySummaryTable.length > 1) {
        await saveTinSurface();
      }
      removeSingleLayerFromMap(this.surveyToDelete.surveyId);

      if (gisModel.view) {
        await DesignContainerVM.loadOriginalSurface();
        if (setStore.triggerSurfaceDesignerVm?.surfaceDef) setStore.triggerSurfaceDesignerVm.surfaceDef = undefined;
        if (setStore.triggerSurfaceDesignerVm?.surfaceDef) console.log('SurfaceDef is triggered.');

        // update the surface elevation layer
        // await DesignContainerVM.updateSurfaceTinElevationLayer();
        // await DesignContainerVM.instance.openOrGenerateSurface(true);
      }
      const i = setStore.selectedSurveysInfo.indexOf(this.surveyToDelete.surveyId);
      if (i !== -1) setStore.selectedSurveysInfo.splice(i, 1);

      if (setStore.currentSurvey.surveyId === id) {
        setStore.resetSOSurvey();
        setStore.resetXYZSurvey();
        setStore.isHeaderAccepted = false;
      }
      getSurveys();
      MessageCenter.clearMessages();
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      if (setStore.surveySummaryTable.length === 1) {
      resetMap();
      }
      setStore.isLoading = false;
    }
  }
  // <-- delete survey dialog

  // warning dialog -->
  @observable warningDialogIsOpen: boolean = false;

  @action.bound toggleWarningDialog() {
    this.warningDialogIsOpen = !this.toggleWarningDialog;
  }

  @action.bound async warningDialogDiscardAction(e: any) {
    // if XYZ survey was opened for editing
    // remove layer if only new survey tab open
    if (!setStore.currentSurvey.surveyId) removeSingleLayerFromMap('new_survey');
    if (setStore.currentSurvey.surveyType === appConfig.hardCodes.surveyTypeXYZ) {
      cancelEditXYZSurvey();
    } else {
      setStore.resetCurrentSurvey();
      setStore.resetStationOffsetObject();
    }
    // if we clicked Edit before warning
    if (this.surveyToEdit.surveyName) {
      await this.openEditSurveyHandler(e, this.surveyToEdit);
      setStore.currentTab = setStore.currentSurvey.surveyId ? 'Edit Survey' : 'New Survey';
    } else {
      // if we clicked Import before warning
      setStore.importSurveyWindowIsOpen = true;
    }
    this.toggleWarningDialog();
  }

  @action.bound warningDialogReturnAction() {
    setStore.currentTab = setStore.currentSurvey.surveyId ? 'Edit Survey' : 'New Survey';
    this.toggleWarningDialog();
  }

  @action.bound async warningDialogSaveAction(e: any) {
    this.toggleWarningDialog();
    if (setStore.currentSurvey.surveyType === appConfig.hardCodes.surveyTypeXYZ) {
      await saveXYZSurvey();
    } else {
      await saveSOSurveyHandler(e);
    }
    if (this.surveyToEdit.surveyName) {
      await this.openEditSurveyHandler(e, this.surveyToEdit);
    } else {
      setStore.importSurveyWindowIsOpen = true;
      getSurveys();
    }
  }
  // <-- warning dialog

  @action.bound async openEditSurveyHandler(e: any, item: any) {
    setStore.surveyEditMode = true;
    if (
      setStore.currentSurvey.surveyName &&
      setStore.currentSurvey.surveyId !== item.surveyId &&
      setStore.isImportingNewSurvey
    ) {
      this.warningDialogIsOpen = true;
      this.surveyToEdit = item;
    } else {
      setStore.isLoading = true;
      setStore.currentSurvey = { ...item };
      setStore.currentSurvey.surveyId = item.surveyId;
      setStore.currentSurvey.surveyName = item.surveyName;
      setStore.currentSurvey.surveyType = item.surveyType;

      // ----------------------------------- 1.XYZ -----------------------------------
      // appConfig.hardCodes.surveyTypeXYZ = 1
      if (item.surveyType === appConfig.hardCodes.surveyTypeXYZ) {
        setStore.currentTab = 'Edit Survey';
        setStore.maxId = 0;
        setStore.initXYZPointsLength = 0;
        setStore.hasSurvey = true;
        setStore.isHeaderAccepted = true;
        setStore.hasGrid = true;

        try {
          setStore.isImportingNewSurvey = false;
          setStore.xyzPointsTable = setStore.surveyEditables.find(
            (s) => s.surveyId === setStore.currentSurvey.surveyId
          )?.groundPoints;
          setStore.initialXyzPointsTable = setStore.surveyEditables.find(
            (s) => s.surveyId === setStore.currentSurvey.surveyId
          )?.groundPoints;

          if (setStore.currentSurvey.surveyId !== "all-surveys") {
            setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
            setStore.triggerBreaklineToolboxVM?.drawGroundPoints();
          }
          if (!setStore.xyzPointsTable) {
            setStore.xyzPointsTable = [];
            setStore.initialXyzPointsTable = [];
          }

          setStore.initXYZPointsLength = setStore.xyzPointsTable.length;
          setStore.maxId = setStore.xyzPointsTable.length;
          MessageCenter.clearMessages();

          const res = await PRJAPI.SpatialRefControllerApi.getSpatialRefByProjectUuidUsingGET(designStore.projectsStore.currentProject.projectUuid);
          designStore.contourInterval=Number(res.data.contourInterval);
        } catch (error) {
          MessageCenter.addError((error as Error).message);
        } finally {
          this.props.startWatchHandler();
          setStore.isLoading = false;
        }
      } else {
        // ---------------------------- 2.Spreadsheet and 3.CrossSlope -------------------------------
        await preloadExistingSurvey(item);
      }
    }
  }

  @action.bound async openEditAllSurvey() {
    setStore.surveyEditMode = true;
    const item = {
      surveyId: 'all-surveys',
      surveyName: 'All Surveys',
      surveyCoordinateSystem: '32164',
      pointsCount: -1,
      surveyDate: '',
      surveyType: 1,
    };
    setStore.isLoading = true;
    setStore.currentSurvey = { ...item };

    if (item.surveyType === appConfig.hardCodes.surveyTypeXYZ) {
      setStore.currentTab = 'Edit Survey';
      setStore.maxId = 0;
      setStore.initXYZPointsLength = 0;
      setStore.hasSurvey = true;
      setStore.isHeaderAccepted = true;
      setStore.hasGrid = true;

      try {
        const allpoints: GroundPoint[] = [];
        for (const survey of setStore.surveyEditables as XyzSurvey[]) {
          allpoints.push(...survey.groundPoints);
        }
        setStore.xyzPointsTable = allpoints;
        setStore.initialXyzPointsTable = allpoints;

        if (!setStore.xyzPointsTable) {
          setStore.xyzPointsTable = [];
          setStore.initialXyzPointsTable = [];
        }

        setStore.initXYZPointsLength = setStore.xyzPointsTable.length;
        setStore.maxId = setStore.xyzPointsTable.length;
        MessageCenter.clearMessages();
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      } finally {
        this.props.startWatchHandler();
        setStore.isLoading = false;
      }
    }
  }

  //newly added row, input fields onChange functionality
  @action.bound handleCheckboxChange(e: any) {
    const index = e.target.value;
    if (e.target.checked) {
      setStore.selectedSurveysInfo.push(setStore.surveySummaryTable[index].surveyId);
    } else {
      setStore.selectedSurveysInfo.replace(
        setStore.selectedSurveysInfo.filter((p) => {
          return !(p === setStore.surveySummaryTable[index].surveyId);
        })
      );
    }
    this.someSurveySelected = setStore.selectedSurveysInfo.length > 0 ? true : false;
    if (setStore.selectedSurveysInfo.length === 0) {
      resetMap();
      const lyr = gisModel.map.findLayerById('SurfaceDesignerVm.GroundPoints');
      if (lyr) lyr.visible = true;
      setStore.pointsOnMap = false;
    }
  }

  @action.bound async onDisplayGroundPoints() {
    setStore.isLoading = true;
    if (setStore.selectedSurveysInfo.length > 0) setStore.pointsOnMap = true;
    if (gisModel.map) {
      for (const survey of setStore.surveySummaryTable) {
        if (setStore.selectedSurveysInfo.includes(survey.surveyId)) {
          await this.displaySurveyDependingOnType(survey);
        } else {
          removeSingleLayerFromMap(survey.surveyId);
        }
      }
    }

    setStore.isLoading = false;
  }

  displaySurveyDependingOnType = async (survey) => {
    if (survey.surveyType === 1) {
      // show XYZ points
      await this.getSurveyPointsAndDisplay(survey.surveyId);
    }
    //SO survey functionality is obsoleted
    /*  else {
      // get SO survey data
      const postJSON = await getSingleSOSurveyData(survey);
      // check if layer already on map
      let layerOnMap;
      for (const lyr of gisModel.map.layers) {
        if (lyr.id === survey.surveyId) {
          layerOnMap = lyr;
          break;
        }
      }
      if (!layerOnMap) {
        // display SO survey with alignment
        setStore.displaySurfaceChecked = false;
        await displaySingleSOSurvey(survey.surveyType, postJSON);
      }
    } */
  };

  getSurveyPointsAndDisplay = async (surveyId) => {
    const turnOn = true;
    const surveyName = setStore.surveySummaryTable.filter((el) => el.surveyId === surveyId)[0].surveyName;
    setStore.isLoading = true;
    try {
      const response: any = await SetSurveyController.getSurveyUsingGET(surveyId);
      displaySurveyPointsClientSide(
        gisModel.view,
        response.data.xyzArray,
        surveyId,
        surveyName,
        turnOn,
        renderer,
        this._currentWkid
      );
      if (setStore.selectedSurveysInfo.length === 0) {
        this.someSurveySelected = false;
      }
      setStore.pointsOnMap = true;
      MessageCenter.clearMessages();
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      setStore.isLoading = false;
    }
  };

  //surface button onClick functionality
  @action.bound async onClickSurface() {
    // let deleteGpSessionId = '';
    // let savedGpSessionId = await DesignContainerVM.getStoredGpSessionId();
    // if ('' !== savedGpSessionId) {
    //   deleteGpSessionId = savedGpSessionId;
    //   savedGpSessionId = '';
    // }
    // if (/* '' === savedGpSessionId && */ false && setStore.selectedSurveysInfo.length === 0) {
    // resetMap();
    // } else {
    setStore.isLoading = true;

    // let surveyIds = '';
    // if ('' === savedGpSessionId) {
    //   const surveyIdsArray: any = [];
    //   for (const element of setStore.selectedSurveysInfo) {
    //     surveyIdsArray.push(element);
    //   }

    //   // surveyIds = `('${element[1]}')`;
    //   const temp = JSON.stringify(surveyIdsArray);
    //   console.log('temp: ', temp);
    //   const t1 = temp.slice(1, temp.length - 1);
    //   const t2 = t1.padStart(t1.length + 1, '(');
    //   /* replaceAll and replace functions works the same but replaceAll is not yet supported in Jest and hence impacting unit tests.
    //    In case if we need to support latest version, we can revert at any point of time. */
    //   // const surveyIds = t2.padEnd(t2.length + 1, ')').replaceAll('"', "'");
    //   surveyIds = t2.padEnd(t2.length + 1, ')').replace(/"/g, "'");
    // }
    // console.log('surveyIds: ', surveyIds);

    try {
      // calling invokeGPService function to call GIS GP service to generate map view for the surface with groundpoints, contour, TIN Triangles and Boundary.
      /**  Please note that the surveyIds argument format is, e.g, "('FAC14897-8090-4DD3-8F1E-1AD937FA3DD4')", or  
        "('D53F295B-71E6-4954-97B3-4163EF8B5F3F', '91885884-B979-4A24-8D71-217736C21E4E')", for multiple surveys*/
      // await invokeGPService(
      //   gisModel.view,
      //   designStore.projectsStore.currentProject.projectUuid,
      //   designStore.projectsStore.currentSessionId,
      //   '',
      //   surveyIds,
      //   appConfig.tempHardCodes.wkid,
      //   '',
      //   '',
      //   designStore.surfaceExpressMode,
      //   0,
      //   { pointsPropertyName: 'projectPoints' },
      //   savedGpSessionId,
      //   deleteGpSessionId
      // );
      // await retrieveOriginalSurface(gisModel.view, designStore.projectsStore.currentSessionId);
      await DesignContainerVM.loadOriginalSurface();

      // update the surface elevation layer
      // await DesignContainerVM.updateSurfaceTinElevationLayer();

      setStore.pointsOnMap = false;
      gisModel.view.ui.add(gisModel.locationWidget, 'bottom-left');
      MessageCenter.clearMessages();
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      setTimeout(() => {
        // designStore.setLoader('');
        setStore.isLoading = false;
      }, 1000);
    }
    // }
  }

  @action.bound importSurveyHandler() {
    if (setStore.currentSurvey.surveyName && setStore.isImportingNewSurvey) {
      // if any survey open for editing show warning dialog
      this.warningDialogIsOpen = true;
    } else {
      setStore.currentSurvey.surveyName = 'New Survey';
      setStore.currentTab = 'New Survey';
      setStore.currentSurvey.surveyType = appConfig.hardCodes.surveyTypeXYZ;
      setStore.isImportingNewSurvey = true;
      setStore.importSurveyWindowIsOpen = true;
      designStore.contourInterval=2;
    }
  }
}

export const removeSingleLayerFromMap = (layerId) => {
  if (gisModel.map) {
    // gisModel.view.ui.remove(gisModel.locationWidget); // ?
    const singleLayerToDelete = gisModel!.map.findLayerById(layerId);
    if (singleLayerToDelete) gisModel?.map.remove(singleLayerToDelete);
  }
};

export const getSurveys = async () => {
  setStore.surveySummaryTable = [];
  setStore.surveyData = [];
  setStore.listOfSOSurveyIds = [];
  setStore.surveyAlignments = [];

  setStore.isLoading = true;
  try {
    const response: any = await SurfaceDefController.getSurfaceDefSurveyMetadataUsingGET(designStore.currentSurface.surfaceDefId)
    if (response.data && response.data.surveyMetadata && response.data.surveyMetadata.length) {
      setStore.surveySummaryTable.push(...response.data.surveyMetadata);
      //surveys that are considered for Alignments
      setStore.surveyData = setStore.surveySummaryTable.filter((item) => item.surveyType !== 1);
      setStore.listOfSOSurveyIds = setStore.surveyData.map((e) => e.surveyId);
      //update the flag to survey if user has survey data available
      setStore.savedSurvey = true;
      setStore.in_designTypeSelection = 'survey';
    }
    if (response.data && response.data.surveyMetadata && response.data.surveyMetadata.length == 0) {
      //   //survey is deleted, has metadata initialized but length is 0
      setStore.savedSurvey = false;
    }
    MessageCenter.clearMessages();
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    setStore.isLoading = false;
  }
};

export const getSingleSOSurveyData = async (item) => {
  let singleSOSurveyData: any = {};
  const getSurveyAPI =
    item.surveyType === appConfig.hardCodes.surveyTypeSpredsheet
      ? 'getStationOffsetSpreadsheetUsingGET'
      : 'getCrossSlopeSurveyByIdUsingGET';

  try {
    const surveyId = item.surveyId;
    // console.log(`${getSurveyAPI}PAYLOAD:`, surveyId);
    const response: any = await SetSurveyStationOffsetController[getSurveyAPI](surveyId);
    // console.log(`${getSurveyAPI}RESPONSE:`, response.data);
    if (response) {
      singleSOSurveyData = {
        ...response.data.alignment,
        ...item,
        alignmentApplyCurves: response.data.alignmentApplyCurves || 'false',
      };
      if (response.data.offsetStations) singleSOSurveyData.offsetStations = response.data.offsetStations;
      if (response.data.crossSlopes) {
        singleSOSurveyData.crossSlopeStations = response.data.crossSlopes;
        singleSOSurveyData.crossSlopeStations.forEach((cs, i) => {
          singleSOSurveyData.crossSlopeStations[i] = {
            alignmentId: response.data.alignment.alignmentId,
            alignmentName: response.data.alignment.alignmentName,
            ...cs,
            crossSlopeStationOffsets: cs.crossSlopeStations,
          };
          delete singleSOSurveyData.crossSlopeStations[i].crossSlopeStations;
          singleSOSurveyData.crossSlopeStations[i].crossSlopeStationOffsets.forEach((el) => {
            delete el.crossSlopeId;
            delete el.crossSlopeStationId;
            delete el.offsets;
          });
        });
      }
      delete singleSOSurveyData.designElementId;
      delete singleSOSurveyData.designElementName;
      delete singleSOSurveyData.designElementTypeId;
      delete singleSOSurveyData.editingStatus;
      delete singleSOSurveyData.pointsCount;
      delete singleSOSurveyData.surveyType;
      delete singleSOSurveyData.alignmentId;
      delete singleSOSurveyData.sessionId;
      delete singleSOSurveyData.surveyDate;
      MessageCenter.clearMessages();
      setStore.isLoading = false;
      return singleSOSurveyData;
    }
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  }
};
